import React from "react";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { PhoneAliasesEditor } from "@/features/contacts/components/PhoneAliasesEditor";
import { JsonFieldCell } from "@/components/shared/JsonFieldCell/JsonFieldCell";
import { TagsFieldCell } from "@/components/shared/TagsFieldCell/TagsFieldCell";
import { OfferingLineItemsCell } from "@/features/offerings/components/OfferingLineItemsEditor";
import type { JsonFieldValue } from "@/components/shared/types";
import type { StaffLineItem, HardCostLineItem } from "@/lib/types/database/offerings";
import type { BaseEntity, EntityWithRelations, FieldValue } from "../../types";
import type { UniversalInlineEditHandler } from "../../types";
import type { SpecializedCellProps } from "./tableCellSpecializedFields.types";

/**
 * Persist a field value via the inline edit handler and mirror it back into
 * the local `data` array so downstream consumers see the updated row.
 * Shared by the JSON / tags / line-item cell renderers.
 */
async function persistFieldUpdate<TData extends BaseEntity>(
  data: TData[],
  item: TData,
  fieldId: string,
  newValue: FieldValue,
  inlineEditHandler: UniversalInlineEditHandler
): Promise<void> {
  await inlineEditHandler({
    id: item.id,
    [fieldId]: newValue,
  });

  const itemIndex = data.findIndex((d) => d.id === item.id);
  if (itemIndex !== -1) {
    data[itemIndex] = { ...data[itemIndex], [fieldId]: newValue };
  }
}

/**
 * EmailAliasesCell renders the email_aliases field with EmailAliasesEditor
 */
export function EmailAliasesCell<TData extends BaseEntity>({
  item,
  data,
}: SpecializedCellProps<TData>) {
  const hasEmail = (value: unknown): value is EntityWithRelations<TData> & { email: string } =>
    typeof value === "object" &&
    value !== null &&
    "email" in value &&
    typeof (value).email === "string" &&
    (value as { email: string }).email.length > 0;

  return (
    <div className="min-w-0">
      {hasEmail(item) ? (
        <EmailAliasesEditor
          contact={item}
          onUpdate={(updated) => {
            // Update the item in the data array
            const itemIndex = data.findIndex((d) => d.id === item.id);
            if (itemIndex !== -1) {
              data[itemIndex] = { ...data[itemIndex], ...updated };
            }
          }}
        />
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </div>
  );
}

/**
 * PhoneAliasesCell renders the phone_aliases field with PhoneAliasesEditor
 */
export function PhoneAliasesCell<TData extends BaseEntity>({
  item,
  data,
}: SpecializedCellProps<TData>) {
  const hasContactId = (value: unknown): value is EntityWithRelations<TData> & { id: string } =>
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof (value as { id: string }).id === "string" &&
    (value as { id: string }).id.length > 0;

  return (
    <div className="min-w-0">
      {hasContactId(item) ? (
        <PhoneAliasesEditor
          contact={item}
          onUpdate={(updated) => {
            const itemIndex = data.findIndex((d) => d.id === item.id);
            if (itemIndex !== -1) {
              data[itemIndex] = { ...data[itemIndex], ...updated };
            }
          }}
        />
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </div>
  );
}

/**
 * OfferingLineItemsCellRenderer renders staff_line_items or hard_cost_line_items with strict types
 */
export function OfferingLineItemsCellRenderer<TData extends BaseEntity>({
  fieldId,
  fieldValue,
  item,
  data,
  inlineEditHandler,
  config,
  fieldConfig,
}: SpecializedCellProps<TData>) {
  const variant = fieldConfig.lineItemsType ?? "staff";
  return (
    <OfferingLineItemsCell
      value={fieldValue as StaffLineItem[] | HardCostLineItem[] | null | undefined}
      fieldId={fieldId}
      entityId={item.id}
      entityName={config.entity}
      variant={variant}
      onSave={async (newValue) => {
        await persistFieldUpdate(data, item, fieldId, newValue, inlineEditHandler);
      }}
      maxDisplayItems={2}
    />
  );
}

/**
 * JsonFieldCell renders JSON type fields (social_links, custom_fields, etc.)
 */
export function JsonFieldCellRenderer<TData extends BaseEntity>({
  fieldId,
  fieldValue,
  item,
  data,
  inlineEditHandler,
  config,
}: SpecializedCellProps<TData>) {
  return (
    <JsonFieldCell
      value={fieldValue as JsonFieldValue}
      fieldId={fieldId}
      entityId={item.id}
      entityName={config.entity}
      onSave={async (newValue) => {
        try {
          await persistFieldUpdate(data, item, fieldId, newValue, inlineEditHandler);
        } catch (error) {
          console.error("Error saving JSON field:", error);
          throw error;
        }
      }}
      maxDisplayItems={2}
    />
  );
}

/**
 * TagsFieldCell renders tags type fields
 */
export function TagsFieldCellRenderer<TData extends BaseEntity>({
  fieldId,
  fieldValue,
  item,
  data,
  inlineEditHandler,
  config,
}: SpecializedCellProps<TData>) {
  return (
    <TagsFieldCell
      value={fieldValue}
      fieldId={fieldId}
      entityId={item.id}
      entityName={config.entity}
      onSave={async (newValue) => {
        try {
          await persistFieldUpdate(data, item, fieldId, newValue, inlineEditHandler);
        } catch (error) {
          console.error("Error saving tags field:", error);
          throw error;
        }
      }}
      maxDisplayItems={2}
    />
  );
}
